// Global debug overlay — fixed top-right toggle + floating bottom-right panel.
// When enabled, surfaces the raw Ollama/job stdout streams for every active
// pending/running job so the user can see what the LLM is actually producing.
//
// State is persisted in localStorage (`nice2job.debug.enabled`) so the toggle
// survives page navigation and refresh. Polling intentionally paused while
// the overlay is off to avoid any perf overhead.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Bug, X, Terminal, ChevronDown, ChevronUp, Square } from 'lucide-react';
import { apiGet } from '../lib/api';
import { useHideMaintainerTools, useProductReady, useSignedIn } from '../lib/useProduct';

interface JobRec {
  id: string;
  cmd: string;
  payload: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  code: number | null;
  updated: number;
  log_bytes?: number;
}

const STORAGE_KEY = 'nice2job.debug.enabled';

function readEnabled(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
}
function writeEnabled(on: boolean) {
  try { localStorage.setItem(STORAGE_KEY, on ? '1' : '0'); } catch { /* noop */ }
}

// ANSI color codes come through in raw Ollama logs — strip for the terminal.
function stripAnsi(s: string): string {
  // eslint-disable-next-line no-control-regex
  return s.replace(/\x1b\[[0-9;]*m/g, '');
}

// Short payload preview for the job header — JSON keys are useful, full
// blobs aren't. Falls back to the first 60 chars of whatever string.
function previewPayload(p: string): string {
  if (!p) return '';
  const t = p.trim();
  if (t.startsWith('{')) {
    try {
      const obj = JSON.parse(t) as Record<string, unknown>;
      const parts: string[] = [];
      if (obj.company) parts.push(String(obj.company));
      if (obj.role) parts.push(String(obj.role));
      if (obj.url && parts.length === 0) parts.push(String(obj.url).slice(0, 40));
      return parts.slice(0, 2).join(' · ') || t.slice(0, 60);
    } catch { /* fall through */ }
  }
  return t.slice(0, 60);
}

export default function DebugOverlay() {
  const [enabled, setEnabled] = useState<boolean>(readEnabled());
  // Raw job internals are a maintainer tool. In the UK-CN student product a
  // floating "Debug" chip over every page reads as an unfinished app, so it
  // follows the same admin gate as the other English maintainer surfaces.
  const hide = useHideMaintainerTools();
  const ready = useProductReady();
  const signedIn = useSignedIn();

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    writeEnabled(next);
  };

  // Anonymous visitors (the public board) must never mount DebugToggle: it
  // polls /api/jobs, whose 401 hard-bounces the page to /login. The admin
  // gate above can't cover this — while the session is still loading,
  // useHideMaintainerTools() is false and the first poll fires anyway.
  if (!ready || !signedIn) return null;
  if (hide) return null;

  return (
    <>
      <DebugToggle enabled={enabled} onToggle={toggle} />
      {enabled && <DebugPanel onClose={toggle} />}
    </>
  );
}

function DebugToggle({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) {
  // Poll /api/jobs even when the panel is closed, so the toggle badge can
  // show a running-count pill. Cheap — just metadata, no SSE connections.
  // Counts running only (consistent with the panel's filter); pending
  // would explode to triple-digits during big Evaluate-all batches and
  // mislead the user into thinking the system is overloaded.
  const [activeCount, setActiveCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await apiGet<{ jobs: JobRec[] }>('/api/jobs');
        if (cancelled) return;
        const n = (res.jobs || []).filter((j) => j.status === 'running').length;
        setActiveCount(n);
      } catch { /* noop */ }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <button
      onClick={onToggle}
      aria-label={enabled ? 'Turn debug mode off' : 'Turn debug mode on'}
      title={enabled ? 'Debug overlay ON — click to close' : `Open debug overlay${activeCount > 0 ? ` · ${activeCount} running` : ''}`}
      style={{
        position: 'fixed',
        top: 18,
        right: 22,
        zIndex: 1000,
        padding: '7px 12px',
        border: `1px solid ${enabled ? '#2d7a4a' : activeCount > 0 ? 'var(--pri)' : 'var(--line)'}`,
        borderRadius: 16,
        background: enabled ? '#2d7a4a' : 'var(--surface)',
        color: enabled ? '#fff' : 'var(--ink2)',
        cursor: 'pointer',
        fontSize: 11.5,
        fontFamily: 'JetBrains Mono, ui-monospace, var(--cjk-sans), monospace',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        boxShadow: enabled ? '0 4px 12px rgba(45,122,74,0.3)' : '0 1px 4px rgba(0,0,0,0.08)',
        transition: 'all 120ms',
      }}
    >
      <Bug size={12} /> {enabled ? 'Debug ON' : 'Debug'}
      {!enabled && activeCount > 0 && (
        <span
          style={{
            background: 'var(--pri)',
            color: '#fff',
            borderRadius: 10,
            padding: '1px 7px',
            fontSize: 10,
            fontWeight: 600,
          }}
        >
          {activeCount}
        </span>
      )}
    </button>
  );
}

function DebugPanel({ onClose }: { onClose: () => void }) {
  const [jobs, setJobs] = useState<JobRec[]>([]);
  const [minimised, setMinimised] = useState(false);
  const [showTerminal, setShowTerminal] = useState<'active' | 'all'>('active');
  const [killArmed, setKillArmed] = useState(false);
  const [busy, setBusy] = useState(false);

  // Poll /api/jobs every 2.5s; stop when panel is closed (DebugPanel unmounts).
  useEffect(() => {
    let cancelled = false;
    const fetchJobs = async () => {
      try {
        const res = await apiGet<{ jobs: JobRec[] }>('/api/jobs');
        if (!cancelled) setJobs(res.jobs || []);
      } catch { /* network hiccup — show stale data */ }
    };
    fetchJobs();
    const id = setInterval(fetchJobs, 2500);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    if (!killArmed) return;
    const t = setTimeout(() => setKillArmed(false), 3000);
    return () => clearTimeout(t);
  }, [killArmed]);

  // 'active' = currently consuming a worker slot (status='running').
  // Capped by MAX_CONCURRENT (typically 5), so the list stays small.
  // Pre-fix: 'active' included status='pending' too — with a 270-URL
  // Evaluate-all batch the panel showed 50 entries (listJobs caps at 50)
  // even though only ~5 were actually running. User reported the
  // confusion: "debug 为什么会现实50".
  const filtered = useMemo(() => {
    const sorted = [...jobs].sort((a, b) => (b.updated || 0) - (a.updated || 0));
    if (showTerminal === 'active') {
      return sorted.filter((j) => j.status === 'running');
    }
    return sorted.slice(0, 20);
  }, [jobs, showTerminal]);

  // Title-bar counts. Split running (occupying an LLM slot now) from
  // waiting (server-queued, will run as a slot frees) so the user sees
  // the truth: "5 running · 268 waiting" instead of "273 active".
  const runningCount = jobs.filter((j) => j.status === 'running').length;
  const waitingCount = jobs.filter((j) => j.status === 'pending').length;
  const activeJobs = jobs.filter((j) => j.status === 'pending' || j.status === 'running');
  const activeCount = activeJobs.length;

  const cancelJob = async (id: string) => {
    try { await fetch(`/api/jobs/${id}`, { method: 'DELETE' }); } catch { /* noop */ }
    // Optimistic: drop from local list immediately; next poll reconciles.
    setJobs((cur) => cur.filter((j) => j.id !== id));
  };

  // Nukes every pending + running task server-side. SIGTERMs each child → all
  // useJobRunner hooks on other pages auto-clear localStorage within ~2s (the
  // next poll returns 404, which the hook handles by removing its key).
  const killAll = async () => {
    if (activeCount === 0 || busy) return;
    if (!killArmed) {
      setKillArmed(true);
      return;
    }
    setKillArmed(false);
    setBusy(true);
    await Promise.all(activeJobs.map((j) =>
      fetch(`/api/jobs/${j.id}`, { method: 'DELETE' }).catch(() => {})
    ));
    setBusy(false);
    setJobs((cur) => cur.filter((j) => j.status !== 'pending' && j.status !== 'running'));
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 18,
        right: 22,
        zIndex: 999,
        width: minimised ? 280 : 560,
        maxWidth: 'calc(100vw - 44px)',
        maxHeight: minimised ? 48 : '70vh',
        background: '#14140f',
        color: '#d0d0c4',
        border: '1px solid #2a2a20',
        borderRadius: 10,
        boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: 'JetBrains Mono, ui-monospace, var(--cjk-sans), monospace',
        transition: 'width 150ms ease, max-height 150ms ease',
      }}
    >
      {/* Titlebar */}
      <div
        style={{
          padding: '8px 12px',
          background: '#1d1d17',
          borderBottom: minimised ? 'none' : '1px solid #2a2a20',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 11.5,
          color: '#bdbdad',
        }}
      >
        <Terminal size={13} />
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
          debug · {runningCount} running
          {waitingCount > 0 && (
            <span style={{ color: '#8a8a78' }}> · {waitingCount} waiting</span>
          )}
        </span>
        {!minimised && (
          <>
            {activeCount > 0 && (
              <button
                onClick={killAll}
                disabled={busy}
                title={killArmed
                  ? `Click again to SIGTERM all ${activeCount} running jobs`
                  : `Stop every running job server-side (${activeCount})`}
                style={{
                  ...btnStyle,
                  fontSize: 10.5,
                  color: killArmed ? '#fff' : '#d47a6a',
                  background: killArmed ? '#8a2e2e' : 'transparent',
                  borderColor: killArmed ? '#8a2e2e' : '#4e2e24',
                }}
              >
                {busy ? 'killing…' : killArmed ? `confirm kill ${activeCount}` : `kill all (${activeCount})`}
              </button>
            )}
            <button
              onClick={() => setShowTerminal((v) => (v === 'active' ? 'all' : 'active'))}
              title="Toggle between running-only and recent-20 view"
              style={{ ...btnStyle, fontSize: 10.5 }}
            >
              {showTerminal === 'active' ? 'running' : 'recent 20'}
            </button>
          </>
        )}
        <button
          onClick={() => setMinimised((v) => !v)}
          title={minimised ? 'Expand' : 'Minimise'}
          style={btnStyle}
        >
          {minimised ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
        <button onClick={onClose} title="Turn debug OFF" style={btnStyle}>
          <X size={12} />
        </button>
      </div>

      {!minimised && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
          {filtered.length === 0 && (
            <div style={{ padding: '24px 16px', color: '#6e6e5e', fontSize: 12 }}>
              {showTerminal === 'active'
                ? (waitingCount > 0
                    ? `No running jobs right now — ${waitingCount} waiting in the queue, will start as worker slots free.`
                    : 'No running jobs. Kick off an evaluate or tailor pass to see live LLM output.')
                : 'No jobs yet.'}
            </div>
          )}
          {filtered.map((j) => (
            <DebugJobRow key={j.id} job={j} onCancel={() => cancelJob(j.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function DebugJobRow({ job, onCancel }: { job: JobRec; onCancel: () => void }) {
  const [expanded, setExpanded] = useState(
    job.status === 'running' || job.status === 'pending',
  );
  const isActive = job.status === 'pending' || job.status === 'running';
  const [text, setText] = useState('');
  const accRef = useRef('');
  const endRef = useRef<HTMLDivElement>(null);

  // Live-tail via SSE while running; replay once + close when terminal.
  useEffect(() => {
    if (!expanded) return;
    accRef.current = '';
    setText('');
    const es = new EventSource(`/api/jobs/${job.id}/stream`);
    es.onmessage = (msg) => {
      try {
        const obj = JSON.parse(msg.data) as { type?: string; text?: string };
        if ((obj.type === 'stdout' || obj.type === 'stderr') && obj.text) {
          accRef.current += obj.text;
          setText(accRef.current);
        }
      } catch { /* skip malformed frames */ }
    };
    es.onerror = () => { es.close(); };
    return () => { es.close(); };
  }, [expanded, job.id]);

  // Auto-scroll terminal to bottom as new tokens stream in.
  useEffect(() => {
    if (!endRef.current) return;
    endRef.current.scrollTop = endRef.current.scrollHeight;
  }, [text]);

  const clean = useMemo(() => stripAnsi(text), [text]);
  const statusColor =
    job.status === 'running' ? '#6fc67a' :
    job.status === 'pending' ? '#d4b94d' :
    job.status === 'done'    ? '#5b8bb5' :
    '#d47a6a'; // failed

  return (
    <div style={{ borderBottom: '1px solid #22221a' }}>
      <div
        onClick={() => setExpanded((v) => !v)}
        style={{
          padding: '8px 12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 11.5,
          color: '#cccebe',
          background: expanded ? '#1a1a14' : 'transparent',
        }}
      >
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
        <span style={{ color: '#9a9a8e', fontSize: 10.5, minWidth: 72 }}>
          {job.status}
        </span>
        <span style={{ color: '#e6e6d4', flexShrink: 0 }}>{job.cmd}</span>
        <span style={{ flex: 1, color: '#8a8a7e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {previewPayload(job.payload)}
        </span>
        <span style={{ color: '#6e6e5e', fontSize: 10 }}>
          {(job.log_bytes ?? 0).toLocaleString()}b
        </span>
        {job.code != null && (
          <span
            style={{
              fontSize: 10,
              padding: '1px 5px',
              background: job.code === 0 ? '#1e3a28' : '#3a1e1e',
              color: job.code === 0 ? '#6fc67a' : '#d47a6a',
              borderRadius: 3,
            }}
          >
            exit {job.code}
          </span>
        )}
        {isActive && (
          <button
            onClick={(e) => { e.stopPropagation(); onCancel(); }}
            title="Kill this job (SIGTERM the child + unlink log files)"
            style={{
              background: 'transparent',
              border: '1px solid #4e2e24',
              color: '#d47a6a',
              borderRadius: 3,
              padding: '1px 5px',
              cursor: 'pointer',
              fontSize: 10,
              fontFamily: 'JetBrains Mono, ui-monospace, var(--cjk-sans), monospace',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Square size={9} /> stop
          </button>
        )}
      </div>
      {expanded && (
        <div
          ref={endRef}
          style={{
            padding: '6px 12px 10px',
            fontSize: 11,
            lineHeight: 1.55,
            color: '#c8c8b8',
            background: '#0e0e0a',
            maxHeight: 260,
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {clean || <span style={{ color: '#5e5e4e', fontStyle: 'italic' }}>warming up…</span>}
        </div>
      )}
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid #2e2e24',
  color: '#bdbdad',
  borderRadius: 4,
  padding: '3px 7px',
  cursor: 'pointer',
  fontSize: 11,
  fontFamily: 'JetBrains Mono, ui-monospace, var(--cjk-sans), monospace',
  display: 'inline-flex',
  alignItems: 'center',
};
